import {
  formatCompactAmPmTime,
  formatDate,
  getPayrollWeekDates,
  isLocalWeekday,
} from "../../../utils/datetime";
import { formatHoursCompact } from "../../../utils/timekeeping";
import { buildCsvString } from "../../../utils/files/csv";

/**
 * Format one worker shift as `7:00am-3:00pm (8 hours)`.
 *
 * @param {Object} shift Compact shift from the week-summary API.
 * @param {string} shift.timeIn Worker clock-in (HH:MM).
 * @param {string} shift.timeOut Worker clock-out (HH:MM).
 * @param {number} shift.hours Worker hours for the shift.
 * @returns {string} Single-shift summary cell text.
 */
export function formatWeekSummaryShift(shift) {
  const hoursLabel = `${formatHoursCompact(shift.hours)} hours`;
  const timeIn = formatCompactAmPmTime(shift.timeIn);
  const timeOut = formatCompactAmPmTime(shift.timeOut);
  if (!timeIn || !timeOut) {
    return hoursLabel;
  }
  return `${timeIn}-${timeOut} (${hoursLabel})`;
}

/**
 * Format a day column header like `Monday 9/14`.
 *
 * @param {Date} date Local calendar date.
 * @returns {string} Weekday plus unpadded month/day.
 */
export function formatWeekSummaryDayHeader(date) {
  const weekday = formatDate(date, "weekday");
  return `${weekday} ${date.getMonth() + 1}/${date.getDate()}`;
}

/**
 * Return whether any worker has a shift on the given ISO date.
 *
 * @param {string} dateKey YYYY-MM-DD.
 * @param {Array<Object>} workers Week-summary worker rows.
 * @returns {boolean} True when at least one shift falls on that day.
 */
function dayHasShifts(dateKey, workers) {
  return workers.some((worker) =>
    (worker.shifts || []).some((shift) => shift.date === dateKey)
  );
}

/**
 * Build day columns for the summary CSV.
 *
 * Monday-Friday always appear. Saturday and Sunday are included only when
 * someone worked that day, so typical weeks match the Mon-Fri manager sheet.
 *
 * @param {string} weekEnding Week-ending date (YYYY-MM-DD).
 * @param {Array<Object>} workers Week-summary worker rows.
 * @returns {Array<{dateKey: string, header: string}>} Day columns in order.
 */
export function getWeekSummaryDayColumns(weekEnding, workers = []) {
  return getPayrollWeekDates(weekEnding)
    .filter((date) => {
      if (isLocalWeekday(date)) {
        return true;
      }
      return dayHasShifts(formatDate(date, "YYYY-MM-DD"), workers);
    })
    .map((date) => ({
      dateKey: formatDate(date, "YYYY-MM-DD"),
      header: formatWeekSummaryDayHeader(date),
    }));
}

/**
 * Join a worker's shifts for one day, or return a dash when none exist.
 *
 * @param {Array<Object>} shifts Shifts on a single day, already ordered.
 * @returns {string} Day cell text.
 */
export function formatWeekSummaryDayCell(shifts = []) {
  if (!shifts.length) {
    return "-";
  }
  return shifts.map(formatWeekSummaryShift).join(" - ");
}

/**
 * Suggested download filename for a payout week summary.
 *
 * @param {string} weekEnding Week-ending date (YYYY-MM-DD).
 * @returns {string} CSV filename.
 */
export function getPayoutWeekSummaryFilename(weekEnding) {
  return `timesheet-summary-week-ending-${weekEnding}.csv`;
}

/**
 * Build CSV text for a payout week's worker-shift summary.
 *
 * @param {Object} payload Week-summary API response.
 * @param {string} payload.weekEnding Saturday week-ending date.
 * @param {Array<Object>} payload.workers Worker rows with compact shifts.
 * @returns {string} CSV document body.
 */
export function buildPayoutWeekSummaryCsv(payload) {
  const workers = payload.workers || [];
  const dayColumns = getWeekSummaryDayColumns(payload.weekEnding, workers);
  const headers = ["Name", "Primary Job", ...dayColumns.map((column) => column.header)];
  const rows = workers.map((worker) => {
    const shiftsByDate = {};
    (worker.shifts || []).forEach((shift) => {
      if (!shift.date) {
        return;
      }
      if (!shiftsByDate[shift.date]) {
        shiftsByDate[shift.date] = [];
      }
      shiftsByDate[shift.date].push(shift);
    });

    const dayCells = dayColumns.map((column) => {
      const dayShifts = (shiftsByDate[column.dateKey] || []).slice().sort((left, right) =>
        String(left.timeIn || "").localeCompare(String(right.timeIn || ""))
      );
      return formatWeekSummaryDayCell(dayShifts);
    });

    return [worker.workerName || "", worker.primaryJob || "-", ...dayCells];
  });

  return buildCsvString(headers, rows);
}
